/**
 * Shared ONNX Runtime Web execution-provider picker.
 *
 * Centralises the WebGPU-vs-wasm decision so every adapter (RTMPose,
 * MotionBERT, VideoPose3D, DepthAnythingV2, OSNet ReID, YOLOv8) picks
 * the same EP for the same browser. Surfaces the picked EP back to
 * callers so the HUD can show "EP: webgpu / wasm" alongside the
 * engine label.
 *
 * docs/migration-plan.md Phase 11+.
 */

export type OnnxExecutionProvider = 'webgpu' | 'wasm';

export interface PickEpOpts {
  /** Some models (e.g. heavyweight Depth Anything base) MUST run on
   *  WebGPU to be usable. Setting this to true keeps the WebGPU
   *  preference even on minor browser quirks; false (default) errs
   *  on the side of safety on Safari. */
  preferWebGpu?: boolean;
  /** When set, force a specific provider regardless of detection.
   *  Used by tests and debugging URL flags (?onnxEp=wasm). */
  override?: OnnxExecutionProvider | null;
}

let lastPicked: OnnxExecutionProvider | null = null;

/**
 * Pick the best available EP. Returns the chosen provider AND the EP
 * list to pass to ort.InferenceSession.create(). The list always ends
 * with 'wasm' so ort can fall back gracefully if the GPU init fails
 * at session-create time.
 */
export function pickOnnxEPs(opts: PickEpOpts = {}): {
  picked: OnnxExecutionProvider;
  list: readonly string[];
} {
  const picked = opts.override ?? autoPick(opts.preferWebGpu ?? true);
  lastPicked = picked;
  const list = picked === 'webgpu' ? (['webgpu', 'wasm'] as const) : (['wasm'] as const);
  return { picked, list };
}

/** The most recently picked provider, for HUD display. null until any
 *  adapter actually requests an EP. */
export function lastPickedOnnxEP(): OnnxExecutionProvider | null {
  return lastPicked;
}

/** Reset cached state. Tests-only. */
export function _resetLastPicked(): void {
  lastPicked = null;
}

function autoPick(preferWebGpu: boolean): OnnxExecutionProvider {
  if (typeof navigator === 'undefined') return 'wasm';

  // URL override: `?onnxEp=wasm` or `?onnxEp=webgpu`. Power-user knob
  // for benchmarking and debugging.
  if (typeof window !== 'undefined' && window.location?.search) {
    const flag = new URLSearchParams(window.location.search).get('onnxEp');
    if (flag === 'wasm' || flag === 'webgpu') return flag;
  }

  if (!preferWebGpu) return 'wasm';

  const ua = navigator.userAgent;
  // Safari's WebGPU implementation in ort-web has known issues at the
  // current version (1.20-1.24). Keep wasm there until upstream
  // confirms parity. Detect Safari but exclude Chrome / Edge / CriOS
  // (which pass through navigator.gpu reliably on macOS / iOS).
  const isSafari =
    /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS\//.test(ua) && !/EdgiOS\//.test(ua);
  if (isSafari) return 'wasm';

  return typeof (navigator as { gpu?: unknown }).gpu !== 'undefined' ? 'webgpu' : 'wasm';
}
